import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarDays, MapPin, Clock, Shirt, Car, ExternalLink, Ticket as TicketIcon, Search, CheckCircle2 } from "lucide-react";
import { formatEventDate } from "@/lib/utils";

/**
 * Public ticket viewer. Guests pull up `/tickets/{code}` on any phone — no login required —
 * to show their QR at the door if they can't find the confirmation email. The QR is only
 * useful when scanned by an authenticated admin at the venue, so this page is safe to share.
 *
 * Route also handles `/tickets` (no code) by rendering a small lookup form.
 */
export default function MyTicket() {
  const { code } = useParams();
  const nav = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(!!code);
  const [error, setError] = useState(null);
  const [lookup, setLookup] = useState("");

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    api.get(`/tickets/code/${code.toUpperCase()}`)
      .then(r => setTicket(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Ticket not found"))
      .finally(() => setLoading(false));
  }, [code]);

  if (!code) {
    return (
      <div className="min-h-[60vh] max-w-md mx-auto px-6 py-16" data-testid="ticket-lookup-page">
        <div className="text-eyebrow">My ticket</div>
        <h1 className="font-heading text-4xl mt-3">Pull up your ticket.</h1>
        <p className="text-neutral-600 mt-4 leading-relaxed">Can't find the confirmation email? Enter your ticket code to show your QR at the door.</p>
        <form
          onSubmit={e=>{ e.preventDefault(); const t = lookup.trim().toUpperCase(); if (!t) return toast.error("Enter your ticket code"); nav(`/tickets/${t}`); }}
          className="mt-6 flex gap-2"
        >
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={lookup}
              onChange={e=>setLookup(e.target.value.toUpperCase())}
              placeholder="Your ticket code"
              className="pl-10 h-12 rounded-full uppercase tracking-widest font-mono"
              autoFocus
              data-testid="ticket-lookup-input"
            />
          </div>
          <Button type="submit" className="h-12 rounded-full bg-black text-white hover:bg-neutral-800 px-6 btn-pill" data-testid="ticket-lookup-submit">Find</Button>
        </form>
        <p className="text-xs text-neutral-500 mt-4">The ticket code is in your confirmation email, printed in gold letters.</p>
      </div>
    );
  }

  if (loading) return <div className="min-h-[60vh] max-w-md mx-auto px-6 py-20 text-neutral-500 text-sm" data-testid="ticket-loading">Loading your ticket…</div>;
  if (error || !ticket) {
    return (
      <div className="min-h-[60vh] max-w-md mx-auto px-6 py-16" data-testid="ticket-not-found">
        <div className="text-eyebrow">My ticket</div>
        <h1 className="font-heading text-4xl mt-3">We couldn't find that one.</h1>
        <p className="text-neutral-600 mt-4 leading-relaxed">The code <span className="font-mono uppercase">{code}</span> didn't match any ticket. Double-check the code in your confirmation email — it's shown in gold letters below the QR.</p>
        <div className="mt-6 flex gap-2">
          <Button onClick={()=>nav("/tickets")} variant="outline" className="rounded-full btn-pill" data-testid="ticket-not-found-retry">Try another code</Button>
          <Button asChild className="rounded-full bg-black text-white btn-pill">
            <Link to="/contact" data-testid="ticket-not-found-contact">Contact us</Link>
          </Button>
        </div>
      </div>
    );
  }

  const ev = ticket.event;
  const dateStr = formatEventDate(ev.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const mapsHref = ev.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ev.venue}, ${ev.address}`)}`
    : null;
  const calendarHref = (() => {
    // Build a Google Calendar quick-add link. Falls back to no link if the date can't be parsed.
    if (!ev.date) return null;
    try {
      const start = new Date(`${ev.date}T19:00:00`);
      const end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
      const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      const params = new URLSearchParams({
        action: "TEMPLATE",
        text: `${ev.title} · Offline & Available`,
        dates: `${fmt(start)}/${fmt(end)}`,
        details: `Ticket code: ${ticket.ticket_code}\nDress code: ${ev.dress_code || "Smart casual"}`,
        location: ev.address || ev.venue,
      });
      return `https://calendar.google.com/calendar/render?${params.toString()}`;
    } catch { return null; }
  })();

  return (
    <div className="min-h-[60vh] bg-cream" data-testid="my-ticket-page">
      <div className="max-w-md mx-auto px-5 py-10 space-y-5">
        {/* Status banner */}
        <div className="text-center">
          <div className="text-eyebrow">You're going to</div>
          <h1 className="font-heading text-3xl md:text-4xl mt-2 leading-tight">{ev.title}</h1>
          {ticket.checked_in && (
            <div className="inline-flex items-center gap-2 mt-4 bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full" data-testid="ticket-checkedin-badge">
              <CheckCircle2 size={14} /> Checked in
              {ticket.checked_in_at && <span className="opacity-80">· {new Date(ticket.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
            </div>
          )}
        </div>

        {/* QR card — the whole reason this page exists */}
        <div className="rounded-3xl bg-white border border-neutral-200 shadow-sm p-6 text-center">
          <div className="text-eyebrow">Scan at the door</div>
          <div className="mt-4 inline-block rounded-2xl bg-white p-3 border border-neutral-100">
            <img
              src={ticket.qr_data_uri}
              alt={`QR code for ticket ${ticket.ticket_code}`}
              className="w-56 h-56 md:w-64 md:h-64 mx-auto"
              data-testid="ticket-qr-image"
            />
          </div>
          <div className="font-mono text-xl text-[hsl(46_65%_52%)] tracking-[4px] mt-5" data-testid="ticket-code-display">{ticket.ticket_code}</div>
          <div className="text-xs text-neutral-500 mt-2">
            Hi {ticket.guest_first_name}, show this QR to our host — they'll scan it on the way in.
          </div>
          {ticket.quantity > 1 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-cream border border-neutral-200 text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full">
              <TicketIcon size={12} /> Admits {ticket.quantity}
            </div>
          )}
        </div>

        {/* Event info */}
        <div className="rounded-2xl bg-white border border-neutral-200 divide-y divide-neutral-100">
          <InfoRow icon={CalendarDays} label="Date" value={dateStr} />
          <InfoRow icon={Clock} label="Time" value={ev.time} />
          <InfoRow icon={MapPin} label="Venue" value={<>{ev.venue}{ev.address && <div className="text-xs text-neutral-500 mt-0.5">{ev.address}</div>}</>} />
          {ev.dress_code && <InfoRow icon={Shirt} label="Dress code" value={ev.dress_code} />}
          {ev.parking_info && <InfoRow icon={Car} label="Parking" value={ev.parking_info} />}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          {mapsHref && (
            <Button asChild variant="outline" className="rounded-full btn-pill" data-testid="ticket-maps-btn">
              <a href={mapsHref} target="_blank" rel="noopener noreferrer"><MapPin size={14} className="mr-2" /> Directions</a>
            </Button>
          )}
          {calendarHref && (
            <Button asChild variant="outline" className="rounded-full btn-pill" data-testid="ticket-cal-btn">
              <a href={calendarHref} target="_blank" rel="noopener noreferrer"><CalendarDays size={14} className="mr-2" /> Add to calendar</a>
            </Button>
          )}
        </div>

        <div className="text-center pt-2">
          <Link to={`/events/${ev.event_id}`} className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1 underline underline-offset-4" data-testid="ticket-event-link">
            See full event details <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="px-5 py-3 flex items-start gap-3">
    <Icon className="gold mt-0.5 shrink-0" size={16} />
    <div className="flex-1 min-w-0">
      <div className="text-eyebrow text-[10px]">{label}</div>
      <div className="text-sm text-neutral-800 mt-0.5">{value}</div>
    </div>
  </div>
);
